import { ErrorComponent } from '@refinedev/antd';
import { NavigateToResource } from '@refinedev/react-router';
import React, { Suspense } from 'react';
import { Outlet, Route, Routes } from 'react-router';

import AuthGuard from './auth/components/auth-guard';
import { ThemedLayoutV2 } from './shared/components/layout';
import { Header } from './shared/components/layout/header';
import { UserRoleEnum } from './users/dto';

// Lazy-loaded pages
const LoginPage = React.lazy(() => import('./login/page'));
const DashboardPage = React.lazy(() => import('./dashboard/page'));
const DevicesPage = React.lazy(() => import('./devices/page'));
const ProfilesPage = React.lazy(() => import('./profiles/page'));
const CarriersPage = React.lazy(() => import('./carriers/page'));
const CustomersPage = React.lazy(() => import('./customers/page'));
const UsersPage = React.lazy(() => import('./users/page'));
const LogsPage = React.lazy(() => import('./logs/page'));
const SettingsPage = React.lazy(() => import('./settings/page'));
const AlertsPage = React.lazy(() => import('./alerts/page'));
const ApplicationVersionsPage = React.lazy(() => import('./applicationVersions/page'));

const ProtectedLayout = ({ roles }: { roles?: UserRoleEnum[] }) => (
  <AuthGuard authParams={roles ? { pageType: 'privateOnly', roles } : undefined}>
    <ThemedLayoutV2 Header={Header}>
      <Suspense>
        <Outlet />
      </Suspense>
    </ThemedLayoutV2>
  </AuthGuard>
);

export const AppRoutes = () => (
  <Routes>
    {/* Login — redirects to the BFF proxy's OIDC login */}
    <Route
      element={
        <Suspense>
          <LoginPage />
        </Suspense>
      }
      path="/login"
    />

    {/* Any authenticated user */}
    <Route element={<ProtectedLayout />}>
      <Route element={<NavigateToResource />} index />
      <Route element={<DashboardPage />} path="/dashboard" />
      <Route element={<LogsPage />} path="/logs" />
      <Route element={<ApplicationVersionsPage />} path="/applicationVersions" />
      <Route element={<SettingsPage />} path="/settings" />
    </Route>

    {/* SimManager + SimOperator */}
    <Route
      element={<ProtectedLayout roles={[UserRoleEnum.SimManager, UserRoleEnum.SimOperator]} />}
    >
      <Route element={<DevicesPage />} path="/devices" />
      <Route element={<ProfilesPage />} path="/profiles" />
      <Route element={<CarriersPage />} path="/carriers" />
    </Route>

    {/* SimAdmin only */}
    <Route element={<ProtectedLayout roles={[UserRoleEnum.SimAdmin]} />}>
      <Route element={<CustomersPage />} path="/customers" />

    </Route>

    {/* SimAdmin + SimManager */}
    <Route element={<ProtectedLayout roles={[UserRoleEnum.SimAdmin, UserRoleEnum.SimManager]} />}>
      <Route element={<UsersPage />} path="/users" />
    </Route>

    {/* All roles */}
    <Route
      element={
        <ProtectedLayout
          roles={[UserRoleEnum.SimManager, UserRoleEnum.SimOperator, UserRoleEnum.SimAdmin]}
        />
      }
    >
      <Route element={<AlertsPage />} path="/alerts" />
    </Route>

    <Route element={<ErrorComponent />} path="*" />
  </Routes>
);
